<script setup lang="ts">
/**
 * Everyone else's caret and selection in a live text box (lib/collabText.ts),
 * drawn over it in their colour with their name on a little flag.
 *
 * A textarea cannot say where index n is on screen, so a hidden mirror copies
 * its box and font, lays the same text out, and the marker's offset in the
 * mirror is the caret's in the textarea. Sits beside the textarea inside a
 * `position: relative` parent; never takes a click.
 */
import { onBeforeUnmount, ref, watch, watchEffect } from "vue";
import { useBoard } from "../stores/board.js";
import type { RemoteSel } from "../lib/collabText.js";

const props = defineProps<{ target: HTMLTextAreaElement | HTMLInputElement | null; sels: RemoteSel[] | undefined }>();
const board = useBoard();

interface Mark {
  userId: string;
  name: string;
  color: string;
  caret: { top: number; left: number; height: number } | null;
  rects: { top: number; left: number; width: number; height: number }[];
}
const marks = ref<Mark[]>([]);
/** Bumped by anything that moves text on screen without changing `sels`. */
const tick = ref(0);
const bump = (): void => void tick.value++;

const COPIED = [
  "boxSizing", "width", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth", "borderStyle",
  "fontFamily", "fontSize", "fontWeight", "fontStyle", "fontVariant", "letterSpacing", "wordSpacing",
  "lineHeight", "textTransform", "textIndent", "tabSize", "textAlign",
] as const;

let mirror: HTMLDivElement | null = null;

function measure(el: HTMLTextAreaElement | HTMLInputElement, sels: RemoteSel[]): Mark[] {
  if (!el.parentElement) return [];
  if (!mirror || mirror.parentElement !== el.parentElement) {
    mirror?.remove();
    mirror = document.createElement("div");
    mirror.setAttribute("aria-hidden", "true");
    el.parentElement.appendChild(mirror);
  }
  const css = getComputedStyle(el);
  const style = mirror.style;
  for (const k of COPIED) style[k] = css[k];
  Object.assign(style, {
    position: "absolute", top: "0", left: "0", visibility: "hidden", pointerEvents: "none",
    whiteSpace: el instanceof HTMLTextAreaElement ? "pre-wrap" : "pre", overflowWrap: "break-word",
    borderColor: "transparent", height: "auto", overflow: "hidden",
    // The textarea's own scrollbar narrows its text; match it.
    width: `${el.clientWidth + parseFloat(css.borderLeftWidth) + parseFloat(css.borderRightWidth)}px`,
  });
  const text = el.value;
  const origin = mirror.getBoundingClientRect();
  const dx = el.offsetLeft - el.scrollLeft;
  const dy = el.offsetTop - el.scrollTop;
  const lineHeight = parseFloat(css.lineHeight) || parseFloat(css.fontSize) * 1.3;
  const out: Mark[] = [];
  for (const { userId, sel } of sels) {
    const user = board.users.get(userId);
    if (!user) continue;
    const [anchor, head] = sel;
    const from = Math.min(anchor, head, text.length);
    const to = Math.min(Math.max(anchor, head), text.length);
    mirror.textContent = "";
    mirror.append(text.slice(0, from));
    const picked = document.createElement("span");
    picked.textContent = text.slice(from, to);
    mirror.append(picked);
    // A zero-width mark where the caret is, so an empty last line still has a place.
    const caretAt = document.createElement("span");
    caretAt.textContent = "​";
    if (head <= anchor) mirror.insertBefore(caretAt, picked);
    else mirror.append(caretAt);
    mirror.append(text.slice(to) || "​");
    // Not laid out (clientHeight 0): nowhere to scroll out of view to.
    const visible = (top: number, height: number): boolean =>
      !el.clientHeight || (top + height > el.offsetTop && top < el.offsetTop + el.clientHeight);
    const rects = from === to ? [] : [...picked.getClientRects()]
      .map((r) => ({ top: r.top - origin.top + dy, left: r.left - origin.left + dx, width: r.width, height: r.height }))
      .filter((r) => visible(r.top, r.height));
    const top = caretAt.offsetTop + dy;
    const caret = !visible(top, lineHeight)
      ? null // Scrolled out of view.
      : { top, left: caretAt.offsetLeft + dx, height: caretAt.offsetHeight || lineHeight };
    out.push({ userId, name: user.displayName, color: user.color, caret, rects });
  }
  mirror.textContent = "";
  return out;
}

watchEffect(() => {
  void tick.value;
  const el = props.target;
  const sels = props.sels ?? [];
  marks.value = el && sels.length ? measure(el, sels) : [];
});

let resize: ResizeObserver | null = null;
watch(
  () => props.target,
  (el, old) => {
    old?.removeEventListener("scroll", bump);
    old?.removeEventListener("input", bump);
    resize?.disconnect();
    if (!el) return;
    el.addEventListener("scroll", bump);
    el.addEventListener("input", bump);
    if (typeof ResizeObserver !== "undefined") {
      resize = new ResizeObserver(bump);
      resize.observe(el);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  props.target?.removeEventListener("scroll", bump);
  props.target?.removeEventListener("input", bump);
  resize?.disconnect();
  mirror?.remove();
});
</script>

<template>
  <div v-if="marks.length" class="remote-carets" aria-hidden="true">
    <template v-for="m in marks" :key="m.userId">
      <div
        v-for="(r, i) in m.rects"
        :key="i"
        class="remote-sel"
        :style="{ top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px`, background: m.color }"
      ></div>
      <div
        v-if="m.caret"
        class="remote-caret"
        :style="{ top: `${m.caret.top}px`, left: `${m.caret.left}px`, height: `${m.caret.height}px`, '--caret-color': m.color }"
      >
        <span class="remote-caret-name">{{ m.name }}</span>
      </div>
    </template>
  </div>
</template>
